import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// The platform admin panel's OWN router (third app, doc/FEATURE_ADMIN_PANEL.md)
// — served only on the admin. host by AdminPagesController. No organization
// context exists here: no org store boot, no requiresOrganization, no cable.
import AdminLayout from '@/components/layout/AdminLayout.vue'
import AdminGuestLayout from '@/components/layout/AdminGuestLayout.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: AdminLayout,
      meta: { requiresAuth: true, requiresPlatformAdmin: true },
      children: [
        {
          path: '',
          name: 'admin-organizations',
          component: () => import('@/views/admin/AdminOrganizationsView.vue'),
        },
        {
          path: 'organizations/:id',
          name: 'admin-organization',
          props: true,
          component: () => import('@/views/admin/AdminOrganizationDetailView.vue'),
        },
      ],
    },
    {
      path: '/',
      // The operator's entrance wears its own chrome — signing into admin.
      // must never look like signing into the console.
      component: AdminGuestLayout,
      meta: { isGuest: true },
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/shared/LoginView.vue'),
        },
        {
          path: 'forgot-password',
          name: 'forgot-password',
          component: () => import('@/views/shared/ForgotPasswordView.vue'),
        },
        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import('@/views/shared/ResetPasswordView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'admin-not-found',
      component: () => import('@/views/admin/AdminNotFoundView.vue'),
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Blocking boot — same contract as the console guard: the first navigation
  // waits for the refresh-cookie session so protected views can read
  // auth.currentUser without null-guards. This host has its own cookie jar.
  if (!auth.isBooted) {
    try {
      if (!auth.isAuthenticated) {
        const refreshed = await auth.tryRefreshToken()
        if (refreshed) await auth.fetchMe()
      }
    } finally {
      auth.isBooted = true
    }
  }

  if (auth.isAuthenticated && !auth.user) {
    await auth.fetchMe()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return '/login'
  }

  // The platform gate: a signed-in non-operator sees « introuvable » — the
  // client-side mirror of the server's 404 Silent Wall.
  if (to.matched.some(r => r.meta.requiresPlatformAdmin) &&
      auth.user?.attributes.platform_admin !== true) {
    return { name: 'admin-not-found' }
  }

  if (to.name === 'login' && auth.isAuthenticated) {
    return '/'
  }
})

export default router
